import { useCallback, useEffect, useRef, useState } from 'react';
import { onReceiveNotification } from '../services/RealtimeService';

/**
 * How many notifications of a given kind arrived while nobody was looking at
 * the screen that answers them.
 *
 * Extracted on its second use rather than its first. The bookings badge had
 * this logic written out in its own provider, and quote requests needed
 * exactly the same thing for exactly the same reason: a professional has to
 * see from the header that something arrived, and the bell's own unread count
 * is no use for it -- it covers every notification type and does not clear
 * when you walk into the one list that deals with these.
 *
 * Deliberately not route-based, and that is the whole subtlety. On a phone,
 * "Mis reservas" and "Reservas entrantes" share one URL -- the toggle between
 * them is component state, not navigation -- so a pathname cannot tell which
 * list is actually on screen. The screen says so itself, on mount and on
 * unmount, through `setViewing`.
 */
export interface UnseenNotifications {
  /** Arrived while the screen was not being looked at. Zero while it is. */
  unseenCount: number;
  /** Called by the screen itself: true on mount, false on unmount. */
  setViewing: (viewing: boolean) => void;
}

export const useUnseenNotifications = (types: readonly string[], enabled: boolean): UnseenNotifications => {
  const [unseenCount, setUnseenCount] = useState(0);
  const viewingRef = useRef(false);

  // The array is rebuilt by every render of the calling provider, so the
  // effect below keys on its contents rather than its identity -- otherwise
  // it would tear down and re-subscribe to the realtime feed on each render.
  const typesKey = types.join(',');

  const setViewing = useCallback((viewing: boolean) => {
    viewingRef.current = viewing;
    if (viewing) setUnseenCount(0);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setUnseenCount(0);
      return;
    }
    const watched = typesKey.split(',');
    return onReceiveNotification((notification) => {
      if (!watched.includes(notification.type)) return;
      if (viewingRef.current) return;
      setUnseenCount((count) => count + 1);
    });
  }, [enabled, typesKey]);

  return { unseenCount, setViewing };
};
